import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../../../utils/createScopedI18n';
import { serviceProtocolI18n } from './i18n';

const REQUIRED_VARIABLES = [
  { key: 'transcription', label: '{{transcription}}' },
  { key: 'protocol', label: '{{protocol}}' },
  { key: 'feeling', label: '{{feeling}}' },
  { key: 'crm_label', label: '{{crm_label}}' },
];

export default function PromptVariantCreateEditModal({
  Modal,
  Input,
  Button,
  Badge,
  show,
  mode = 'create',
  initialData = null,
  onClose,
  onSave,
}) {
  const { language } = useLanguage();
  const serviceProtocolLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: serviceProtocolI18n });
  const [form, setForm] = useState({
    name: '',
    customInstructions: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    serviceProtocolI18n.changeLanguage(serviceProtocolLanguage);
  }, [serviceProtocolLanguage]);

  useEffect(() => {
    if (!show) return;
    if (mode === 'edit' && initialData) {
      setForm({
        name: initialData.name ?? '',
        customInstructions: initialData.customInstructions ?? '',
      });
    } else {
      setForm({
        name: '',
        customInstructions: '',
      });
    }
  }, [show, mode, initialData]);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const isValid = useMemo(() => {
    const hasName = form.name.trim().length >= 3;
    const hasInstructions = form.customInstructions.trim().length > 0;
    return hasName && hasInstructions;
  }, [form.name, form.customInstructions]);

  const isDirty = useMemo(() => {
    if (mode === 'create') {
      return (
        form.name.trim().length > 0 ||
        form.customInstructions.trim().length > 0
      );
    }
    return (
      form.name !== (initialData?.name ?? '') ||
      form.customInstructions !== (initialData?.customInstructions ?? '')
    );
  }, [form, mode, initialData]);

  const handleClose = () => {
    if (isDirty) {
      const confirmed = window.confirm(
        t('service_protocol_prompt_modal_confirm_discard')
      );
      if (!confirmed) return;
    }
    onClose?.();
  };

  const handleSave = async () => {
    if (!isValid || saving) return;
    setSaving(true);

    const payload = {
      id: initialData?.id ?? null,
      name: form.name.trim(),
      customInstructions: form.customInstructions.trim(),
      variablesConfig: REQUIRED_VARIABLES.map((v) => v.key),
    };

    try {
      await onSave?.(payload);
      setSaving(false);
    } catch (e) {
      console.error('Error saving prompt variant', e);
      setSaving(false);
    }
  };

  const primaryLabel =
    mode === 'edit'
      ? t('service_protocol_prompt_modal_save_edit')
      : t('service_protocol_prompt_modal_save_create');

  if (!Modal) return null;

  return (
    <Modal
      title={
        mode === 'edit'
          ? t('service_protocol_prompt_modal_edit_title')
          : t('service_protocol_prompt_modal_create_title')
      }
      show={show}
      onClose={handleClose}
      primaryBtnTxt={primaryLabel}
      primaryBtnAction={handleSave}
      disabledPrimaryBtn={!isValid || saving}
      secondaryBtnTxt={t('service_protocol_prompt_modal_cancel')}
      secondaryBtnAction={handleClose}
      width={760}
    >
      <div style={{ display: 'grid', gap: 16 }}>
        {/* Variables obligatorias */}
        <div
          style={{
            background: 'var(--gray-50, #f9fafb)',
            border: '1px solid var(--gray-200, #e5e7eb)',
            borderRadius: 8,
            padding: '12px 16px',
          }}
        >
          <p className='label mb-2'>
            {t('service_protocol_prompt_modal_required_vars')}
          </p>
          <div className='d-flex flex-wrap gap-1'>
            {REQUIRED_VARIABLES.map((v) =>
              Badge ? (
                <Badge key={v.key} text={v.label} color='primary' size='sm' />
              ) : (
                <span
                  key={v.key}
                  style={{
                    display: 'inline-block',
                    background: 'var(--primary-100, #dbeafe)',
                    color: 'var(--primary-700, #1d4ed8)',
                    borderRadius: 4,
                    padding: '2px 8px',
                    fontSize: '0.8rem',
                    fontFamily: 'monospace',
                  }}
                >
                  {v.label}
                </span>
              )
            )}
          </div>
          <p
            className='caption font-light mb-0 mt-2'
            style={{ fontSize: '0.75rem' }}
          >
            {t('service_protocol_prompt_modal_required_hint')}
          </p>
        </div>

        {/* Nombre */}
        {Input ? (
          <Input
            label={t('service_protocol_prompt_modal_name_label')}
            placeholder={t('service_protocol_prompt_modal_name_placeholder')}
            value={form.name}
            onChange={(e) => setField('name', e.target.value)}
          />
        ) : (
          <div>
            <label className='label mb-1 d-block'>
              {t('service_protocol_prompt_modal_name_label')}
            </label>
            <input
              className='form-control'
              placeholder={t('service_protocol_prompt_modal_name_placeholder')}
              value={form.name}
              onChange={(e) => setField('name', e.target.value)}
            />
          </div>
        )}

        {/* Custom Instructions */}
        <div>
          <label className='label mb-1 d-block'>
            {t('service_protocol_prompt_modal_custom_label')}
          </label>
          <textarea
            className='form-control'
            rows={8}
            placeholder={t('service_protocol_prompt_modal_custom_placeholder')}
            value={form.customInstructions}
            onChange={(e) => setField('customInstructions', e.target.value)}
            style={{
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              resize: 'vertical',
            }}
          />
        </div>

        {/* Indicador dirty */}
        {isDirty && (
          <div
            style={{
              background: 'var(--warning-100, #fef3c7)',
              border: '1px solid var(--warning-300, #fcd34d)',
              borderRadius: 8,
              padding: '8px 12px',
              fontSize: '0.8rem',
              color: 'var(--warning-700, #92400e)',
            }}
          >
            {t('service_protocol_prompt_modal_dirty')}
          </div>
        )}
      </div>
    </Modal>
  );
}
